/**
 * CommunicatieInsightsWidget — compacte "Communicatie vandaag" tegel.
 *
 * Sprint P0.13 · Communicatie Sprint (20 Feb 2026).  Toont bij het
 * hoofd-dashboard hoeveel herinneringen zijn verzonden vandaag, hoeveel
 * betaallinks zijn geopend, hoeveel betalingen zijn ontvangen, en of er
 * bounced e-mails wachten.
 *
 * Endpoint: GET /api/admin/communications/kpis
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../context/AuthContext";
import { Send, MousePointerClick, CreditCard, Bell, AlertTriangle, MessageSquare, ChevronRight } from "lucide-react";
import { AdminSectionCard } from "./ui";
import { nlTerm } from "../../utils/pluralize";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function CommunicatieInsightsWidget({ onOpenCommunicatie }) {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/communications/kpis");
      setKpis(r.data);
    } catch {
      // stil falen — widget mag geen dashboard breken
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminSectionCard
      title="Communicatie vandaag"
      subtitle={loading ? "Laden…" : "Klik voor volledig communicatiecentrum →"}
      actions={
        <MessageSquare className="w-4 h-4 text-brand-500" />
      }
      testid="communicatie-insights-widget"
    >
      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-8 w-1/3 bg-slate-200 rounded" />
          <div className="h-3 w-1/2 bg-slate-100 rounded" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniKpi
              icon={Send}
              label="Verzonden"
              value={kpis?.sent_today ?? 0}
              sub={nlTerm("reminder", kpis?.sent_today ?? 0)}
              tone="brand"
              testid="comm-widget-sent"
            />
            <MiniKpi
              icon={MousePointerClick}
              label="Geopend"
              value={kpis?.opened_today ?? 0}
              sub={nlTerm("session", kpis?.opened_today ?? 0)}
              tone="amber"
              testid="comm-widget-opened"
            />
            <MiniKpi
              icon={CreditCard}
              label="Betaald"
              value={kpis?.paid_today ?? 0}
              sub={nlTerm("payment", kpis?.paid_today ?? 0)}
              tone="emerald"
              testid="comm-widget-paid"
            />
            <MiniKpi
              icon={Bell}
              label="Open"
              value={kpis?.open_reminders ?? 0}
              sub={nlTerm("reminder", kpis?.open_reminders ?? 0)}
              tone="slate"
              testid="comm-widget-open"
            />
          </div>

          {(kpis?.bounced_open ?? 0) > 0 && (
            <div className="mt-3 border-l-4 border-red-500 bg-red-50 p-3 flex items-start gap-2 text-xs text-red-900" data-testid="comm-widget-bounced">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-chivo font-bold">
                  {nlTerm("message", kpis.bounced_open)}
                </span> met bounce-fout — controleer in het Communicatiecentrum.
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center justify-between text-xs text-ink-500">
            <span>Laatste: {fmtDate(kpis?.last_sent_at)}</span>
            <button
              type="button"
              onClick={onOpenCommunicatie}
              className="inline-flex items-center gap-1 text-brand-500 hover:text-brand-700 font-chivo font-bold"
              data-testid="comm-widget-link"
            >
              Open Communicatie <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </>
      )}
    </AdminSectionCard>
  );
}

function MiniKpi({ icon: Icon, label, value, sub, tone, testid }) {
  const toneCls = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    brand: "bg-brand-50 border-brand-100 text-brand-900",
    red: "bg-red-50 border-red-200 text-red-900",
  }[tone] || "bg-slate-50 border-slate-200 text-slate-900";
  return (
    <div className={`rounded-xl border p-3 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.14em] font-chivo font-bold opacity-70">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-60" />
      </div>
      <div className="mt-1 text-lg font-chivo font-bold font-mono-num tabular-nums">{value ?? "—"}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}
